import { z } from 'zod';

import { AddressSchema } from '../address';
import { PaymentCryptoOnchainSchema } from '../payment/type';
import { DeviceSchema, PriceSchema, SubscriptionSchema } from '../shared';

function resolveOrderCurrency(order: {
  currency?: string | null;
  summaries?: ReadonlyArray<unknown> | null;
  store?: { chargeCurrencies?: unknown; presentmentCurrencies?: unknown } | null;
}): string {
  if (order.currency) return order.currency;

  const firstSummary = order.summaries?.[0] as { currency?: string | null } | undefined;
  if (firstSummary?.currency) return firstSummary.currency;

  const charge = order.store?.chargeCurrencies;
  if (Array.isArray(charge) && typeof charge[0] === 'string' && charge[0]) return charge[0];

  const presentment = order.store?.presentmentCurrencies;
  if (Array.isArray(presentment) && typeof presentment[0] === 'string' && presentment[0]) return presentment[0];

  return 'USD';
}

export const OrderSchema = z
  .object({
    id: z.string(),
    orderNumber: z.number().nullable(),
    customer: z.object({
      id: z.string().optional(),
      firstName: z.string().nullable().default('-'),
      lastName: z.string().nullable().default('-'),
      email: z.string().nullable().default('-'),
      phone: z.string().nullable().optional(),
    }),
    store: z.object({
      id: z.string().optional(),
      name: z.string().default('-'),
      chargeCurrencies: z.array(z.string()).optional(),
      presentmentCurrencies: z.array(z.string()).optional(),
    }),
    status: z.string(),
    draft: z.boolean().default(false),
    createdFrom: z.string().optional(),
    externalOrderId: z.string().nullable().optional(),
    checkoutSessionId: z.string().nullable().optional(),
    items: z.array(z.any()),
    payment: z.any().optional(),
    summaries: z.array(z.any()).optional(),
    paidAmount: z.number().nullable().default(-1),
    currency: z.string().nullable(),
    createdAt: z.coerce.date().default(new Date()),
    updatedAt: z.coerce.date().nullable().optional(),
  })
  .transform((order) => ({
    ...order,
    currency: resolveOrderCurrency(order),
  }));

const CustomerDetailSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  externalCustomerId: z.string().nullable().optional(),
  billingAddress: AddressSchema.nullable().optional(),
  shippingAddress: AddressSchema.nullable().optional(),
  locale: z.string().nullable().optional(),
  acceptsMarketing: z.boolean().default(false),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  device: DeviceSchema.nullable().optional(),
});

const OrderLineItemProductSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().default('-'),
  storefrontProductId: z.string().nullable().optional(),
  originalProductId: z.string().nullable().default('-'),
});

const OrderLineItemVariantSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number().nullable(),
  imageUrl: z.string().nullable(),
  description: z.string().nullable().default('-'),
  storefrontVariantId: z.string().nullable().default('-'),
  originalVariantId: z.string().nullable().default('-'),
});

const OrderLineItemSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  productId: z.string().nullable().default('-'),
  variantId: z.string().nullable().default('-'),
  priceId: z.string().nullable().default('-'),
  sku: z.string().optional(),
  unitAmount: z.number(),
  quantity: z.number(),
  amount: z.number(),
  adjustedAmount: z.number(),
  adjustments: z.any().optional(),
  recurring: z.boolean().default(false),
  rebillMode: z.string().nullable().optional(),
  rebillStepIntervalMs: z.number().nullable().optional(),
  interval: z.enum(['day', 'week', 'month', 'year']).nullable(),
  intervalCount: z.number().nullable(),
  totalBillingCycles: z.number().nullable(),
  unitAmountAfterFirstCycle: z.number().nullable(),
  subscriptionSettings: z.any().optional(),
  orderLineItemProduct: OrderLineItemProductSchema.nullable(),
  orderLineItemVariant: OrderLineItemVariantSchema.nullable(),
  currency: z.string(),
  properties: z.record(z.any()),
});

const AdjustementSchema = z.object({
  amount: z.number().optional(),
  currency: z.string().nullable().optional(),
  description: z.string().nullable(),
  level: z.string().nullable().optional(),
  rate: z.number().nullable().optional(),
  sourceId: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  // Included-in-price tax (#TAG-3345): `included` on the Tax line, `includedTaxAmount` on order promotions.
  included: z.boolean().optional(),
  includedTaxAmount: z.number().optional(),
});

const OrderSummarySchema = z.object({
  id: z.string(),
  orderId: z.string(),
  items: z.any().optional(),
  adjustments: z.array(AdjustementSchema).optional(),
  subtotalAmount: z.number(),
  lineItemsPromotionAmount: z.number(),
  subtotalAdjustedAmount: z.number(),
  totalPromotionAmount: z.number(),
  lineItemsTaxAmount: z.number(),
  totalTaxAmount: z.number(),
  shippingCost: z.number(),
  totalAmount: z.number(),
  totalAdjustedAmount: z.number(),
  totalWeight: z.number().default(0),
  currency: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const PromotionUsageSchema = z.object({
  id: z.string(),
  promotionId: z.string(),
  orderId: z.string(),
  usedAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const CheckoutSessionSchema = z.object({
  id: z.string(),
  sessionId: z.string().nullable(),
  cartToken: z.string().nullable().default('-'),
  checkoutToken: z.string(),
  customerId: z.string(),
  status: z.string().optional(),
  billingAddress: AddressSchema.nullable(),
  shippingAddress: AddressSchema.nullable(),
  checkoutUrl: z.string(),
  returnUrl: z.string().nullable(),
  selectedPresentmentCurrency: z.string().nullable(),
  shippingRate: z.object({ shippingRateName: z.string() }).nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const ErrorSchema = z
  .object({
    code: z.string().optional(),
    message: z.string().optional(),
    processorCode: z.string().optional(),
    processorMessage: z.string().optional(),
  })
  .nullable()
  .optional();

const ProcessorSchema = z
  .object({
    name: z.string(),
    id: z.string(),
    type: z.string().optional(),
  })
  .nullable()
  .optional();

const TransactionSchema = z.object({
  id: z.string(),
  type: z.string(),
  status: z.string(),
  amount: z.number(),
  result: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date().nullable().optional(),
  externalTransactionId: z.string().nullable(),
  authorizationCode: z.string().nullable().default('-'),
  // Additional fields for complete transaction details
  processor: ProcessorSchema,
  processorId: z.string().nullable().optional(),
  accountId: z.string().nullable().optional(),
  paymentId: z.string().nullable().optional(),
  storeId: z.string().nullable().optional(),
  storefrontTransactionId: z.string().nullable().optional(),
  paymentInstrumentId: z.string().nullable().optional(),
  usedProcessorTokenId: z.string().nullable().optional(),
  networkTransactionId: z.string().nullable().optional(),
  isTest: z.boolean().nullable().optional(),
  error: ErrorSchema,
  rawRequest: z.any().nullable().optional(),
  rawResponse: z.any().nullable().optional(),
  initiatedBy: z.string().nullable().optional(),
  reasonType: z.string().nullable().optional(),
  draft: z.boolean().nullable().optional(),
  metadata: z.any().nullable().optional(),
  requireAction: z.any().nullable().optional(),
  currency: z.string().nullable().optional(),
  avsResponse: z.any().nullable().optional(),
  cvvResponse: z.any().nullable().optional(),
});

const PaymentCustomerSchema = z.object({
  id: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
});

// Lightweight projection of the payment_instruments row used by the
// embedded Payments table on the Order Details page. Card details are
// optional because non-card instruments (zelle, custom_payment, …) don't
// populate them. Mirrors the richer Payment.Payment schema in the
// payments entity but keeps only what the order page actually renders.
const PaymentInstrumentCardSchema = z.object({
  brand: z.string().nullable().optional(),
  last4: z.string().nullable().optional(),
  bin: z.string().nullable().optional(),
  exp_month: z.number().nullable().optional(),
  exp_year: z.number().nullable().optional(),
}).nullable().optional();

const PaymentInstrumentTypeSchema = z.object({
  type: z.string(),
  card: PaymentInstrumentCardSchema,
});

const PaymentDetailSchema = z.object({
  id: z.string(),
  amount: z.number(),
  refundedAmount: z.number().optional(),
  status: z.string(),
  subStatus: z.string().nullish(),
  orderId: z.string(),
  customerId: z.string(),
  paymentInstrumentId: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  customer: PaymentCustomerSchema,
  transactions: z.array(TransactionSchema),
  paymentInstrument: PaymentInstrumentTypeSchema.nullable().optional(),
  metadata: z.record(z.any()).nullable().optional(),
  /** Set by the order detail reads on an on-chain crypto payment only: its coin, network and testnet flag. */
  cryptoOnchain: PaymentCryptoOnchainSchema.nullable().optional(),
});

const StoreDetailSchema = z.object({
  name: z.string(),
  type: z.string().nullable().optional(),
  chargeCurrencies: z.any().optional(),
  presentmentCurrencies: z.any().optional(),
});

const RelatedOrderItemSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  unitAmount: z.number(),
  quantity: z.number(),
  amount: z.number(),
  adjustedAmount: z.number(),
  orderLineItemProduct: OrderLineItemProductSchema.nullable(),
  orderLineItemVariant: OrderLineItemVariantSchema.nullable(),
});

const RelatedOrderPaymentSchema = z.object({
  id: z.string(),
  status: z.string(),
  subStatus: z.string().nullish(),
  amount: z.number(),
  paymentInstrumentId: z.string().nullable().optional(),
});

const RelatedOrderSummarySchema = z.object({
  id: z.string(),
  totalAmount: z.number(),
  subtotalAmount: z.number(),
  totalAdjustedAmount: z.number(),
});

const RelatedOrderSchema = z.object({
  id: z.string(),
  storeId: z.string(),
  accountId: z.string().optional(),
  orderNumber: z.number().nullable(),
  status: z.string(),
  customerId: z.string(),
  paidAmount: z.number().nullable(),
  paidAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  metadata: z.any().optional(),
  items: z.array(RelatedOrderItemSchema),
  summaries: z.array(RelatedOrderSummarySchema),
  payments: z.array(RelatedOrderPaymentSchema),
});

export const OrderDetailSchema = z
  .object({
    id: z.string(),
    storeId: z.string(),
    orderNumber: z.number().nullable(),
    accountId: z.string(),
    status: z.string(),
    cancelledAt: z.coerce.date().nullable(),
    cancelledReason: z.string().nullable(),
    paidAt: z.coerce.date().nullable(),
    paidAmount: z.number().nullable(),
    openAt: z.coerce.date(),
    abandonedAt: z.coerce.date().nullable(),
    currency: z.string().nullable(),
    externalCustomerType: z.string().nullable(),
    externalCustomerId: z.string().nullable().default('-'),
    externalOrderId: z.string().nullable(),
    billingAddress: AddressSchema.nullable(),
    shippingAddress: AddressSchema.nullable(),
    pickupAddress: z.any().optional(),
    taxesIncluded: z.boolean().default(false),
    draft: z.boolean().default(false),
    checkoutSessionId: z.string(),
    sessionHash: z.string().nullable(),
    customerId: z.string(),
    createdFrom: z.string().optional(),
    paymentInstrumentId: z.string().optional().nullable(),
    refundedAt: z.coerce.date().nullable().optional(),
    refundedAmount: z.number().nullable().optional(),
    metadata: z.any().optional(),
    noteAttributes: z
      .record(z.string(), z.union([z.string(), z.number()]))
      .nullable()
      .optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    customer: CustomerDetailSchema,
    items: z.array(OrderLineItemSchema),
    summaries: z.array(OrderSummarySchema),
    promotions: z.array(PromotionUsageSchema),
    checkoutSession: CheckoutSessionSchema.optional(),
    payments: z.array(PaymentDetailSchema),
    subscriptions: z.array(SubscriptionSchema),
    store: StoreDetailSchema,
    relatedOrders: z.array(RelatedOrderSchema),
  })
  .transform((order) => ({
    ...order,
    currency: resolveOrderCurrency(order),
  }));

export type Order = z.infer<typeof OrderSchema>;
export type OrderDetail = z.infer<typeof OrderDetailSchema>;

export const OrderDto = (object: unknown): Order => OrderSchema.parse(object);
export const OrderDetailDto = (object: unknown): OrderDetail => OrderDetailSchema.parse(object);
